import React, { JSX, useEffect } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { ILegalEntityGroupFormValues, initialLegalEntityGroupFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { useLEGroupSchema } from 'pages/LegalEntitiesPage/schemaValidation';
import { useScroll } from 'hooks/useScroll';

export interface ILegalEntityGroupForm {
  onCancel?: () => void;
  onSubmit: (arg: ILegalEntityGroupFormValues | any) => void;
  onNameChange?: (arg: string) => void;
  initialFormValues?: ILegalEntityGroupFormValues | null;
  children?: JSX.Element;
  isSubmitLoading: boolean;
  setIsSubmitLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const LegalEntityGroupForm = ({
  onCancel,
  onSubmit,
  onNameChange,
  initialFormValues,
  children,
  setIsSubmitLoading,
  isSubmitLoading,
}: ILegalEntityGroupForm) => {
  // const { id } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { leGroupSchema } = useLEGroupSchema();
  const { elRef, scrollToError } = useScroll();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ILegalEntityGroupFormValues>({
    defaultValues: initialFormValues || initialLegalEntityGroupFormValues,
    resolver: yupResolver(leGroupSchema) as unknown as Resolver<ILegalEntityGroupFormValues>,
  });

  const watchName = watch('name');

  useEffect(() => {
    if (onNameChange) {
      onNameChange(watchName);
    }
  }, [watchName]);

  useEffect(() => {
    reset(initialFormValues || initialLegalEntityGroupFormValues);
  }, [reset, initialFormValues]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  const onSubmitForm = async (data: any) => {
    setIsSubmitLoading(true);
    if (initialFormValues) {
      await onSubmit({
        ...initialFormValues,
        ...data,
      });
      setIsSubmitLoading(false);
    } else {
      await onSubmit(data);
      setIsSubmitLoading(false);
    }
    setIsSubmitLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Grid container ref={elRef}>
        <Grid size={{ xs: 12 }} container columnSpacing={3}>
          <Grid size={{ xs: 12, lg: 8, xl: 6 }}>
            <FormInput
              name="name"
              label={`${t('addGroup.groupName.label')}*`}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>{children}</Grid>
          <Grid
            container
            columnSpacing={3}
            size={{ xs: 12, lg: 8, xl: 6 }}
            sx={{ rowGap: 3, mt: 6 }}
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                type="submit"
                fullWidth
                id="save-button"
                label={t('general.save.button')}
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
              />
            </Grid>

            {onCancel && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  fullWidth
                  id="cancel-button"
                  label={t('general.cancel.button')}
                  variant={EButtonVariants.outlined}
                  onClick={onCancel}
                />
              </Grid>
            )}
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export { LegalEntityGroupForm };
